import React, { useEffect, useState, useCallback } from "react";
import { api, formatApiError } from "@/lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Building2, RefreshCw, Unplug, PlugZap, CheckCircle2, Webhook, Copy, Eye, EyeOff } from "lucide-react";
import CoreLogo from "@/components/CoreLogo";
import { toast } from "sonner";
import { formatDate, formatUSD, formatUSDCompact } from "@/lib/format";

export default function CoreIntegrationPanel({ compact = false, onChange }) {
  const [status, setStatus] = useState(null);
  const [dashboard, setDashboard] = useState(null);
  const [open, setOpen] = useState(false);
  const [creds, setCreds] = useState({ email: "agent@corerealty.com", password: "" });
  const [busy, setBusy] = useState(false);
  const [syncing, setSyncing] = useState(false);

  const loadStatus = useCallback(async () => {
    try {
      const res = await api.get("/integrations/core/status");
      setStatus(res.data);
      if (res.data.connected) {
        try {
          const d = await api.get("/integrations/core/dashboard");
          setDashboard(d.data);
        } catch (err) {
          console.warn("CORE dashboard fetch failed:", err);
          setDashboard(null);
        }
      } else {
        setDashboard(null);
      }
    } catch (err) {
      console.warn("CORE status fetch failed:", err);
    }
  }, []);

  useEffect(() => { loadStatus(); }, [loadStatus]);

  const connect = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.post("/integrations/core/connect", creds);
      toast.success("Connected to CORE Realty");
      setOpen(false);
      setCreds({ email: creds.email, password: "" });
      await loadStatus();
      if (onChange) onChange();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setBusy(false);
    }
  };

  const sync = async () => {
    setSyncing(true);
    try {
      const res = await api.post("/integrations/core/sync");
      toast.success(`Synced ${res.data.total} deals (${res.data.inserted} new, ${res.data.updated} updated)`);
      await loadStatus();
      if (onChange) onChange();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSyncing(false);
    }
  };

  const disconnect = async () => {
    if (!window.confirm("Disconnect from CORE Realty? Synced deals will remain in your Ledger but won't update.")) return;
    try {
      await api.delete("/integrations/core");
      toast.success("Disconnected");
      await loadStatus();
      if (onChange) onChange();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  if (!status) return null;

  // Compact: render small Dashboard widget
  if (compact) {
    if (!status.connected) {
      return (
        <div className="border border-neutral-200 p-5 bg-white" data-testid="core-widget-disconnected">
          <div className="flex items-center justify-between mb-1">
            <div className="text-neutral-900" data-testid="core-widget-logo">
              <CoreLogo variant="mark" size={36} />
            </div>
            <span className="font-mono text-[10px] uppercase tracking-[0.3em] text-neutral-400">Not Connected</span>
          </div>
          <div className="font-serif text-xl mt-2">CORE Realty</div>
          <p className="text-sm text-neutral-500 mt-1 mb-4">Pull deals and GCI from your brokerage back office.</p>
          <Button onClick={() => setOpen(true)} className="rounded-none bg-neutral-900 hover:bg-neutral-800 text-white w-full h-9" data-testid="core-widget-connect-button">
            <PlugZap className="h-4 w-4 mr-2" strokeWidth={1.5} />
            Connect
          </Button>
          <ConnectDialog open={open} setOpen={setOpen} creds={creds} setCreds={setCreds} onSubmit={connect} busy={busy} />
        </div>
      );
    }
    // Connected widget
    return (
      <div className="border border-neutral-200 bg-white" data-testid="core-widget-connected">
        <div className="p-5 pb-3">
          <div className="flex items-center justify-between mb-1">
            <div className="text-neutral-900">
              <CoreLogo variant="mark" size={36} />
            </div>
            <span className="inline-flex items-center gap-1 font-mono text-[10px] uppercase tracking-[0.3em] text-emerald-700">
              <CheckCircle2 className="h-3 w-3" strokeWidth={1.5} />
              Live
            </span>
          </div>
          <div className="font-serif text-xl mt-2">CORE Realty</div>
          <div className="font-mono text-xs text-neutral-500 mt-0.5">{status.core_user_name} · {status.core_email}</div>
        </div>
        {dashboard ? (
          <div className="grid grid-cols-2 border-t border-neutral-200">
            <div className="px-5 py-3 border-r border-neutral-200">
              <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-neutral-500 mb-1">GCI YTD</div>
              <div className="font-mono text-xl">{formatUSDCompact(dashboard.gci_ytd)}</div>
            </div>
            <div className="px-5 py-3">
              <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-neutral-500 mb-1">GCI Goal</div>
              <div className="font-mono text-xl">{formatUSDCompact(dashboard.gci_goal)}</div>
            </div>
            <div className="px-5 py-3 border-r border-t border-neutral-200">
              <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-neutral-500 mb-1">Pipeline</div>
              <div className="font-mono text-xl">{formatUSDCompact(dashboard.pipeline_value)}</div>
            </div>
            <div className="px-5 py-3 border-t border-neutral-200">
              <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-neutral-500 mb-1">Progress</div>
              <div className="font-mono text-xl" style={{ color: "var(--profit)" }}>{dashboard.gci_progress_pct.toFixed(1)}%</div>
            </div>
          </div>
        ) : null}
        {dashboard && (
          <div className="px-5 py-3 border-t border-neutral-200">
            <div className="h-1 w-full bg-neutral-100">
              <div className="h-1 bg-neutral-900" style={{ width: `${Math.min(100, dashboard.gci_progress_pct)}%` }} />
            </div>
            <div className="flex justify-between mt-1 font-mono text-[10px] text-neutral-500">
              <span>{formatUSD(dashboard.gci_ytd)}</span>
              <span>{formatUSD(dashboard.gci_goal)}</span>
            </div>
          </div>
        )}
        <div className="border-t border-neutral-200 p-4 flex items-center justify-between gap-2">
          <span className="font-mono text-[10px] text-neutral-400">
            {status.last_sync_at ? `Last sync ${formatDate(status.last_sync_at)}` : "Never synced"}
          </span>
          <Button onClick={sync} disabled={syncing} className="rounded-none bg-neutral-900 hover:bg-neutral-800 text-white h-8 text-xs" data-testid="core-widget-sync-button">
            <RefreshCw className={`h-3.5 w-3.5 mr-1.5 ${syncing ? "animate-spin" : ""}`} strokeWidth={1.5} />
            {syncing ? "Syncing…" : "Sync now"}
          </Button>
        </div>
      </div>
    );
  }

  // Full panel (Integrations page)
  return (
    <div className="border border-neutral-200" data-testid="core-integration-panel">
      <div className="p-6 flex items-start justify-between">
        <div className="flex items-start gap-4">
          <div className="text-neutral-900" data-testid="core-panel-logo">
            <CoreLogo variant="mark" size={56} />
          </div>
          <div>
            <div className="font-serif text-2xl">CORE Realty &amp; Associates</div>
            <p className="text-sm text-neutral-500 mt-1 max-w-xl">
              Connect your brokerage back office to sync deals and surface live GCI on your Ledger dashboard.
            </p>
            {status.connected && (
              <div className="mt-3 inline-flex items-center gap-2 font-mono text-[10px] uppercase tracking-[0.3em] text-emerald-700">
                <CheckCircle2 className="h-3 w-3" strokeWidth={1.5} /> Connected as {status.core_email}
              </div>
            )}
          </div>
        </div>
        <div className="flex gap-2">
          {status.connected ? (
            <>
              <Button onClick={sync} disabled={syncing} className="rounded-none bg-neutral-900 hover:bg-neutral-800 text-white" data-testid="core-sync-button">
                <RefreshCw className={`h-4 w-4 mr-2 ${syncing ? "animate-spin" : ""}`} strokeWidth={1.5} />
                {syncing ? "Syncing…" : "Sync deals"}
              </Button>
              <Button onClick={disconnect} variant="outline" className="rounded-none border-neutral-300 hover:bg-neutral-50" data-testid="core-disconnect-button">
                <Unplug className="h-4 w-4 mr-2" strokeWidth={1.5} />
                Disconnect
              </Button>
            </>
          ) : (
            <Button onClick={() => setOpen(true)} className="rounded-none bg-neutral-900 hover:bg-neutral-800 text-white" data-testid="core-connect-button">
              <PlugZap className="h-4 w-4 mr-2" strokeWidth={1.5} />
              Connect CORE
            </Button>
          )}
        </div>
      </div>

      {status.connected && dashboard && (
        <div className="grid grid-cols-2 md:grid-cols-4 border-t border-neutral-200">
          <KPI label="GCI YTD" value={formatUSD(dashboard.gci_ytd)} testid="core-kpi-gci" />
          <KPI label="GCI Goal" value={formatUSD(dashboard.gci_goal)} testid="core-kpi-goal" />
          <KPI label="Progress" value={`${dashboard.gci_progress_pct.toFixed(1)}%`} testid="core-kpi-progress" />
          <KPI label="Pipeline Value" value={formatUSD(dashboard.pipeline_value)} testid="core-kpi-pipeline" />
        </div>
      )}
      {status.connected && status.last_sync_at && (
        <div className="border-t border-neutral-200 px-6 py-3 font-mono text-[10px] uppercase tracking-[0.2em] text-neutral-500 flex justify-between">
          <span>Last sync: {formatDate(status.last_sync_at)}</span>
          <span>{status.last_sync_count} deal(s) on last sync</span>
        </div>
      )}

      {status.connected && status.webhook_id && (
        <WebhookSection webhookId={status.webhook_id} webhookSecret={status.webhook_secret} />
      )}

      <ConnectDialog open={open} setOpen={setOpen} creds={creds} setCreds={setCreds} onSubmit={connect} busy={busy} />
    </div>
  );
}

function WebhookSection({ webhookId, webhookSecret }) {
  const [revealed, setRevealed] = useState(false);
  const backend = process.env.REACT_APP_BACKEND_URL;
  const url = `${backend}/api/webhooks/core/${webhookId}`;
  const copy = async (val, label) => {
    try {
      await navigator.clipboard.writeText(val);
      toast.success(`${label} copied`);
    } catch {
      toast.error("Copy failed");
    }
  };
  return (
    <div className="border-t border-neutral-200 p-6" data-testid="webhook-section">
      <div className="flex items-center gap-2 mb-3">
        <Webhook className="h-4 w-4 text-neutral-700" strokeWidth={1.5} />
        <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-neutral-500">Real-time Sync (Webhook)</div>
      </div>
      <p className="text-sm text-neutral-600 mb-4 max-w-2xl">
        Configure CORE Realty to POST deal events to this endpoint for instant sync. Body events: <span className="font-mono text-xs">deal.upserted</span>, <span className="font-mono text-xs">deal.deleted</span>. Sign the request body with HMAC-SHA256 and send it in the <span className="font-mono text-xs">X-Webhook-Signature</span> header.
      </p>
      <div className="space-y-3">
        <div>
          <Label className="text-[10px] uppercase tracking-[0.2em] text-neutral-500">Endpoint URL</Label>
          <div className="mt-1 flex items-center gap-2">
            <code className="flex-1 font-mono text-xs bg-neutral-50 border border-neutral-200 px-3 py-2 truncate" data-testid="webhook-url">{url}</code>
            <Button type="button" onClick={() => copy(url, "URL")} variant="outline" className="rounded-none border-neutral-300 hover:bg-neutral-100 h-9" data-testid="webhook-copy-url">
              <Copy className="h-3.5 w-3.5" strokeWidth={1.5} />
            </Button>
          </div>
        </div>
        <div>
          <Label className="text-[10px] uppercase tracking-[0.2em] text-neutral-500">Signing Secret</Label>
          <div className="mt-1 flex items-center gap-2">
            <code className="flex-1 font-mono text-xs bg-neutral-50 border border-neutral-200 px-3 py-2 truncate" data-testid="webhook-secret">
              {revealed ? webhookSecret : "•".repeat(Math.min(32, webhookSecret?.length || 32))}
            </code>
            <Button type="button" onClick={() => setRevealed((r) => !r)} variant="outline" className="rounded-none border-neutral-300 hover:bg-neutral-100 h-9" data-testid="webhook-toggle-secret">
              {revealed ? <EyeOff className="h-3.5 w-3.5" strokeWidth={1.5} /> : <Eye className="h-3.5 w-3.5" strokeWidth={1.5} />}
            </Button>
            <Button type="button" onClick={() => copy(webhookSecret, "Secret")} variant="outline" className="rounded-none border-neutral-300 hover:bg-neutral-100 h-9" data-testid="webhook-copy-secret">
              <Copy className="h-3.5 w-3.5" strokeWidth={1.5} />
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}

function KPI({ label, value, testid }) {
  return (
    <div className="px-6 py-4 border-r border-neutral-200 last:border-r-0" data-testid={testid}>
      <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-neutral-500 mb-1">{label}</div>
      <div className="font-mono text-2xl">{value}</div>
    </div>
  );
}

function ConnectDialog({ open, setOpen, creds, setCreds, onSubmit, busy }) {
  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogContent className="rounded-none border-neutral-300 max-w-md" data-testid="core-connect-dialog">
        <DialogHeader>
          <DialogTitle className="font-serif text-2xl">Connect to CORE Realty</DialogTitle>
          <DialogDescription className="text-neutral-500 text-sm">
            Sign in with the same email and password you use for the CORE agent dashboard.
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={onSubmit} className="space-y-4">
          <div>
            <Label className="text-[10px] uppercase tracking-[0.2em] text-neutral-500">CORE Email</Label>
            <Input type="email" required value={creds.email} onChange={(e) => setCreds({ ...creds, email: e.target.value })}
              className="mt-2 rounded-none border-neutral-300 focus-visible:ring-0 focus-visible:border-neutral-900" data-testid="core-email-input" />
          </div>
          <div>
            <Label className="text-[10px] uppercase tracking-[0.2em] text-neutral-500">CORE Password</Label>
            <Input type="password" required value={creds.password} onChange={(e) => setCreds({ ...creds, password: e.target.value })}
              className="mt-2 rounded-none border-neutral-300 focus-visible:ring-0 focus-visible:border-neutral-900" data-testid="core-password-input" />
          </div>
          <div className="text-xs text-neutral-500 border-l-2 border-neutral-300 pl-3 leading-relaxed">
            Demo credentials: <span className="font-mono">agent@corerealty.com / CoreAgent2026</span>
          </div>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => setOpen(false)} className="rounded-none border-neutral-300" data-testid="core-connect-cancel-button">
              Cancel
            </Button>
            <Button type="submit" disabled={busy} className="rounded-none bg-neutral-900 hover:bg-neutral-800 text-white" data-testid="core-connect-submit-button">
              {busy ? "Connecting…" : "Connect"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
